import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MapProvider as MapProviderType, MapProviderContext, AdminMapSettings } from './MapInterface';
import api from '../../services/api';

// Create the context
const MapContext = createContext<MapProviderContext | undefined>(undefined);

// Custom hook to use the map context
export const useMapProvider = (): MapProviderContext => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapProvider must be used within a MapProvider');
  }
  return context;
};

interface MapProviderProps {
  children: ReactNode;
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const [provider, setProviderState] = useState<MapProviderType>('google');
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Load initial provider preference - use environment variable by default
  useEffect(() => {
    loadMapProviderPreference();
  }, []);

  const loadMapProviderPreference = async () => {
    try {
      const response = await api.get('/settings/map-provider');
      const adminProvider = response.data?.provider;
      
      if (adminProvider) {
        setProviderState(adminProvider);
        console.log(`Using admin-configured map provider: ${adminProvider}`);
      } else {
        // Fall back to environment variable
        const defaultProvider = (process.env.REACT_APP_DEFAULT_MAP_PROVIDER as MapProviderType) || 'google';
        setProviderState(defaultProvider);
        console.log(`Using environment default map provider: ${defaultProvider}`);
      }
    } catch (err: any) {
      console.warn('Could not load admin map provider preference:', err.message);
      // Only use environment default as final fallback
      const defaultProvider = (process.env.REACT_APP_DEFAULT_MAP_PROVIDER as MapProviderType) || 'google';
      setProviderState(defaultProvider);
      console.log(`Fallback to environment default map provider: ${defaultProvider}`);
    } finally {
      setIsLoaded(true);
    }
  };

  // Admin-only function to change provider globally
  const setProvider = async (newProvider: MapProviderType) => {
    try {
      setError(undefined);
      
      // Save preference to backend
      const response = await api.post('/admin/settings/map-provider', {
        provider: newProvider
      });

      if (response.data.success) {
        setProviderState(newProvider);
        console.log(`Map provider changed to: ${newProvider}`);
      } else {
        throw new Error(response.data.error || 'Failed to save map provider preference');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to change map provider';
      setError(errorMessage);
      console.error('Error changing map provider:', errorMessage);
      
      // Show user-friendly error
      // You might want to use a toast notification here
      alert(`Error changing map provider: ${errorMessage}`);
    }
  };

  const contextValue: MapProviderContext = {
    provider,
    setProvider,
    isLoaded,
    error
  };

  return (
    <MapContext.Provider value={contextValue}>
      {children}
    </MapContext.Provider>
  );
};

// HOC for components that need map provider context
export const withMapProvider = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => (
    <MapProvider>
      <Component {...props} />
    </MapProvider>
  );
};